import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { ClientValidationErrorModel } from "client/Api/common";
import { WebDavEvent, publish } from "client_legacy/Documents/WebDav/events";
import { EnabledServer as WebDavEnabledServer } from "client/Features/Documents/ItemPicker/WebDavPicker/ItemSelector";
import { copy } from "client/Api/Documents/WebDav/webDavActions";
import {
	copyNotConfiguredMessage,
	Destination,
	WebDavThingsToCopy,
	ThingsToCopy,
	CopyNameConfiguration,
	convertToCrossStoreDestination,
	WebDavDestination
} from "./common";
import { CopyResult, DocumentStoreType } from "client/Api/Documents/common";
import {
	copy as copyCrossStore,
	WebDavCrossStoreItemModel,
	CopyAcrossDataStoresModel
} from "client/Api/Documents/CrossStoreOperations/crossStoreActions";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { HttpRequestError } from "client/Api/httpClient";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";

class WebDavCopyImplementer {

	public computeNameConfigurationSettings(thingsToCopy: WebDavThingsToCopy): CopyNameConfiguration {

		if (thingsToCopy.documents.length !== 1) {
			return { shouldShow: false };
		}

		const { isFolder, name } = thingsToCopy.documents[0];

		return {
			shouldShow: true,
			isFolder,
			supportsExtension: !isFolder,
			name
		};
	}

	public getEnabledServer(
		thingsToCopy: ThingsToCopy
	): WebDavEnabledServer {
		const { allowCrossStoreOperations } = documentsConfiguration();

		switch (thingsToCopy.storeKind) {
			case DocumentStoreType.SharePoint:
				if (allowCrossStoreOperations) {
					return {
						kind: "all"
					};
				}
				return {
					kind: "none",
					reason: copyNotConfiguredMessage
				};
			case DocumentStoreType.WebDav:
				if (this.canBeCopiedDirectly(thingsToCopy)) {
					return {
						kind: "all"
					};
				}
				if (allowCrossStoreOperations) {
					return {
						kind: "all"
					};
				}
				return {
					kind: "none",
					reason: copyNotConfiguredMessage
				};
			default:
				throw new UnreachableCaseError(thingsToCopy);
		}
	}

	public copy(
		thingsToCopy: WebDavThingsToCopy,
		destination: Destination,
		name: string | null,
		signal: AbortSignal
	): Promise<CopyResult> {
		const { allowCrossStoreOperations } = documentsConfiguration();
		switch (destination.storeKind) {
			case DocumentStoreType.SharePoint:
				if (allowCrossStoreOperations) {
					return this.copyInTheBackground(
						thingsToCopy,
						destination,
						name,
						signal
					);
				}

				throw new Error("Does not support copying to SharePoint");
			case DocumentStoreType.WebDav:
				if (this.canBeCopiedDirectly(thingsToCopy)) {
					return this.copyDirectly(
						thingsToCopy,
						destination,
						name,
						signal
					);
				}

				if (allowCrossStoreOperations) {
					return this.copyInTheBackground(
						thingsToCopy,
						destination,
						name,
						signal
					);
				}

				throw new Error("No Copy operation is available for the things which need to be copied");
			default:
				throw new UnreachableCaseError(destination);
		}
	}

	private canBeCopiedDirectly(thingsToCopy: WebDavThingsToCopy): boolean {
		const { documents } = thingsToCopy;
		return documents.length === 1 && !documents[0].isFolder;
	}

	private async copyDirectly(
		thingsToCopy: WebDavThingsToCopy,
		destination: WebDavDestination,
		name: string | null,
		signal: AbortSignal
	): Promise<CopyResult> {
		const item: FileSystemItemModel = thingsToCopy.documents[0];

		const { serverId: destinationServerId, folderId: destinationFolderId } = destination.target;

		const sourceServerId = thingsToCopy.source
			.folder
			.server
			.id;
		try {
			const createdFile: FileSystemItemModel = await copy(
				sourceServerId,
				{
					destinationServerId,
					destinationFolderId,
					fileId: item.id,
					name: name
				},
				signal
			);

			publish(WebDavEvent.CreatedOrReplaced, { serverId: destinationServerId, item: createdFile });
			return { kind: "success" };
		} catch (error) {
			if (isCancelledError(error)) {
				throw error;
			}

			let validationErrors: readonly ClientValidationErrorModel[] = [];
			if (error instanceof HttpRequestError && error.statusCode === 400) {
				try {
					validationErrors = error.readJson<readonly ClientValidationErrorModel[]>();
				} catch (exception) {
					console.error(exception);
				}
			}

			return { kind: "failed", errors: validationErrors };
		}
	}

	private async copyInTheBackground(
		thingsToCopy: WebDavThingsToCopy,
		destination: Destination,
		name: string | null,
		signal: AbortSignal
	): Promise<CopyResult> {
		const documents: readonly FileSystemItemModel[] = thingsToCopy.documents;

		const itemsToCopy: WebDavCrossStoreItemModel[] = documents.map(it => ({
			id: it.id,
			serverId: thingsToCopy.source
				.folder
				.server
				.id
		}));

		const data: CopyAcrossDataStoresModel = {
			items: {
				storeType: DocumentStoreType.WebDav,
				webDavItems: itemsToCopy
			},
			destination: convertToCrossStoreDestination(destination),
			newName: name
		};
		try {
			await copyCrossStore(data, signal);
			return { kind: "scheduled" };

		} catch (error) {
			if (isCancelledError(error)) {
				throw error;
			}

			const validationErrors: ClientValidationErrorModel[] = [];
			if (error instanceof HttpRequestError && error.statusCode=== 400) {
				const responseErrors = error.readJson<ClientValidationErrorModel[]>();
				validationErrors.push(...responseErrors);
			}
			return { kind: "failed", errors: validationErrors };
		}
	}
}

export {
	WebDavCopyImplementer
};
